import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';

import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { Subject, takeUntil, filter } from 'rxjs';
import { CompanyService, Company } from '../../../services/company.service';
import { LoggerService } from '../../../services/logger.service';
import { SaveButtonComponent } from '../../save-button/save-button.component';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmSwitchImports } from '@spartan-ng/helm/switch';

/**
 * E-mail notification preferences.
 *
 * Only preferences the company can change live in the form. Mails that are
 * always sent (security notices such as the blocked redemption PIN) are listed
 * read-only so it is clear what to expect in the inbox.
 */
@Component({
  selector: 'app-notification-settings',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    SaveButtonComponent,
    HlmCardImports,
    HlmItemImports,
    HlmBadgeImports,
    HlmSwitchImports,
  ],
  templateUrl: './notification-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./notification-settings.component.css'],
})
export class NotificationSettingsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private initialFormValues: any;
  loading = false;
  formChanged = false;
  company: Company | null = null;

  @ViewChild('saveButton') saveButton!: SaveButtonComponent;

  notificationsForm: FormGroup;

  constructor(
    private companyService: CompanyService,
    private fb: FormBuilder,
    private logger: LoggerService,
  ) {
    this.notificationsForm = this.fb.group({
      notify_on_sale_email: [true],
    });

    this.notificationsForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.checkFormChanged());
  }

  ngOnInit() {
    this.companyService.company$
      .pipe(
        takeUntil(this.destroy$),
        filter((c) => c !== null),
      )
      .subscribe((company) => {
        this.company = company;
        this.populateForm(company);
      });
  }

  private checkFormChanged() {
    if (this.initialFormValues) {
      this.formChanged = !Object.keys(this.initialFormValues).every(
        (key) => this.initialFormValues[key] === this.notificationsForm.get(key)?.value,
      );
    }
  }

  private populateForm(company: Company) {
    this.notificationsForm.patchValue(
      {
        // undefined (older API) means the backend default: enabled
        notify_on_sale_email: company.notify_on_sale_email !== false,
      },
      { emitEvent: false },
    );
    this.initialFormValues = this.notificationsForm.value;
    this.formChanged = false;
  }

  onSubmit() {
    if (!this.notificationsForm.valid || this.loading) return;
    this.loading = true;
    this.saveButton.hideMessages();
    this.companyService.updateCompanyDetails(this.notificationsForm.value).subscribe({
      next: () => {
        this.loading = false;
        this.initialFormValues = this.notificationsForm.value;
        this.formChanged = false;
        this.saveButton.showSuccessMessage();
      },
      error: (error) => {
        const errorMessage =
          error.error?.detail || 'Fehler beim Speichern. Bitte versuche es erneut.';
        this.logger.error('Error saving notification settings:', error);
        this.loading = false;
        this.saveButton.showErrorMessage(errorMessage);
      },
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
